import React, { useEffect, useState, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polygon, useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import ChatBox from "./ChatBox";
import UserLink from "./UserLink";
import { diveVisibilityMeta } from "../utils/visibility";
import AiAssistant from "./AiAssistant";
import SiteConditions from "./SiteConditions";
import MapSidePanel from "./MapSidePanel";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/api";
import { linkify } from "../utils/text";

// Assistant IA masqué temporairement (clé API à régler). Repasser à true pour le réactiver.
const AI_ENABLED = false;

// ---------- Icônes (HTML + CSS) ----------
// Rouge = site sans plongée
const redIcon = new L.DivIcon({
  className: "sd-marker sd-marker--red",
  html: `<div class="sd-dot sd-dot--red"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Bleu = site avec plongée
const blueIcon = new L.DivIcon({
  className: "sd-marker sd-marker--blue",
  html: `
    <div class="sd-pulse">
      <div class="sd-pulse__dot"></div>
      <div class="sd-pulse__ring"></div>
    </div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Vert = site où l'utilisateur participe à une plongée
const greenIcon = new L.DivIcon({
  className: "sd-marker sd-marker--green",
  html: `
    <div class="sd-pulse">
      <div class="sd-pulse__dot"></div>
      <div class="sd-pulse__ring"></div>
    </div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const poiIcon = new L.DivIcon({
  className: "sd-marker sd-marker--poi",
  html: `
    <div class="poi-pin">
      <div class="poi-pin__dot"></div>
    </div>
  `,
  iconSize: [30, 30],
  iconAnchor: [13, 26], // pointe en bas
  popupAnchor: [0, -24],
});

// Icône position utilisateur
function SwissMask() {
  const [coords, setCoords] = React.useState(null);

  // Moteur de rendu avec une grande marge : le masque couvre largement au-delà
  // de l'écran, donc il n'est pas redessiné à chaque déplacement (fini le flicker).
  const maskRenderer = React.useMemo(() => L.svg({ padding: 4 }), []);

  React.useEffect(() => {
    fetch("/switzerland.geojson")
      .then((r) => r.json())
      .then((data) => {
        const coords = data.features[0].geometry.coordinates[0].map(([lon, lat]) => [lat, lon]);
        setCoords(coords);
      })
      .catch((err) => console.error("Erreur chargement GeoJSON Suisse :", err));
  }, []);

  if (!coords) return null;

  const world = [
    [90, -180], [90, 180], [-90, 180], [-90, -180]
  ];
  const swissHole = [...coords].reverse();

  return (
    <Polygon
      positions={[world, swissHole]}
      renderer={maskRenderer}
      pathOptions={{
        fillColor: "rgba(255,255,255,0.9)",
        fillOpacity: 0.70,
        stroke: false,
      }}
      interactive={false}
    />
  );
}

// Coordonnées approximatives du centre de chaque canton (pour centrer la carte
// par défaut sur la région de l'utilisateur, d'après le canton de son profil).
const CANTON_CENTERS = {
  AG: [47.39, 8.05], AI: [47.32, 9.42], AR: [47.38, 9.28], BE: [46.82, 7.62],
  BL: [47.45, 7.73], BS: [47.56, 7.59], FR: [46.70, 7.12], GE: [46.21, 6.14],
  GL: [46.98, 9.07], GR: [46.66, 9.58], JU: [47.35, 7.16], LU: [47.07, 8.14],
  NE: [46.99, 6.75], NW: [46.93, 8.39], OW: [46.85, 8.25], SG: [47.35, 9.35],
  SH: [47.70, 8.62], SO: [47.28, 7.64], SZ: [47.05, 8.66], TG: [47.57, 9.09],
  TI: [46.33, 8.80], UR: [46.77, 8.65], VD: [46.57, 6.55], VS: [46.20, 7.55],
  ZG: [47.16, 8.52], ZH: [47.42, 8.65],
};
const CANTON_ZOOM = 9;

// Conditions : couleurs "clarté de l'eau" + helpers pour la pastille popup
const daysSince = (dateStr) => {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date();
  return Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - d) / 86400000);
};
const relativeDays = (dateStr, lang) => {
  if (!dateStr) return "";
  const days = daysSince(dateStr);
  const l = (lang || "fr").slice(0, 2);
  if (days <= 0) return l === "de" ? "heute" : l === "en" ? "today" : "aujourd'hui";
  if (days === 1) return l === "de" ? "gestern" : l === "en" ? "yesterday" : "hier";
  return l === "en" ? `${days} d ago` : l === "de" ? `vor ${days} T` : `il y a ${days} j`;
};

function SwissView({ isMobile, bounds, userCenter }) {
  const map = useMap();
  const didInit = React.useRef(false);
  const didUserCenter = React.useRef(false);

  useEffect(() => {
    // Retire le préfixe "Leaflet" de l'attribution (on garde "© OpenStreetMap", requis).
    if (map.attributionControl) map.attributionControl.setPrefix(false);

    // Sur mobile : vue centrée plus rapprochée (afficher toute la Suisse rend
    // la carte minuscule sur un petit écran). Sur desktop : on ajuste aux bounds.
    const MOBILE_CENTER = [46.8, 8.23];
    const MOBILE_ZOOM = 8;
    const applyView = () => {
      // Priorité : centrer sur le canton de l'utilisateur si on le connaît.
      if (userCenter) {
        map.setView(userCenter, CANTON_ZOOM, { animate: false });
        didUserCenter.current = true;
      } else if (isMobile) {
        map.setView(MOBILE_CENTER, MOBILE_ZOOM, { animate: false });
      } else {
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 10, animate: false });
      }
    };

    // seulement au premier mount
    if (!didInit.current) {
      didInit.current = true;
      applyView();

      // On réapplique après invalidateSize une fois le layout stabilisé
      // (mobile : barres iOS ; desktop : nav + header qui décalent le conteneur).
      const t = setTimeout(() => {
        map.invalidateSize();
        applyView();
      }, 150);
      return () => clearTimeout(t);
    }

    // Le canton est arrivé après le 1er montage → on recentre une seule fois.
    if (userCenter && !didUserCenter.current) {
      didUserCenter.current = true;
      map.setView(userCenter, CANTON_ZOOM, { animate: false });
    }
  }, [map, isMobile, bounds, userCenter]);

  return null;
}

// Fonction pour transformer les URLs en liens cliquables dans l'IB description

const Map = ({ currentUser, filterDate, filterCanton, filterType, filterGas, filterConfig, isFiltersPanelOpen }) => {
  const { t, i18n } = useTranslation();
  const position = [46.8182, 8.2275];
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedDive, setSelectedDive] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // Création rapide : recherche d'un site pour planifier directement
  const [showCreateSearch, setShowCreateSearch] = useState(false);
  const [createSearch, setCreateSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Ouvre la recherche de création si l'URL contient ?create=1
  // (déclenché par le "+" de la barre mobile ou le bouton "Planifier" du header desktop)
  useEffect(() => {
    if (searchParams.get("create") === "1") {
      setShowCreateSearch(true);
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openPlanForSite = (site) => {
    setShowCreateSearch(false);
    setCreateSearch("");
    setEditingDive(null);
    setSelectedSite(site);
    setShowModal(true);
  };
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showSiteDetails, setShowSiteDetails] = useState(false);
  const [editingDive, setEditingDive] = useState(null);
  const [changingSite, setChangingSite] = useState(false); // "Changer de site" en édition
  const [siteSearch, setSiteSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [pois, setPois] = useState([]);
  const [showPois, setShowPois] = useState(false); // toggle (off par défaut)
  const [poiDropdownOpen, setPoiDropdownOpen] = useState(false);
  const [poiFilters, setPoiFilters] = useState([]); // ex: ["gonflage", "location"]
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [userCenter, setUserCenter] = useState(null); // centre carte selon le canton du profil
  const [latestConditions, setLatestConditions] = useState({}); // { site_id: dernier rapport }
  const mapRef = useRef(null); // instance Leaflet (pour flyTo depuis le panneau)
  const markerRefs = useRef({}); // { site_id: marker } pour ouvrir popup/tooltip depuis le panneau
  const hoveredRef = useRef(null); // site survolé (impératif, pas d'état → pas de re-render)
  const [panelSiteId, setPanelSiteId] = useState(null); // site affiché en fiche dans le panneau desktop
  const [panelCollapsed, setPanelCollapsed] = useState(false); // panneau desktop replié
  const [myClubsForForm, setMyClubsForForm] = useState([]); // clubs pour la visibilité "mon club"
  const poiDropdownRef = useRef(null);

  const filteredUsers = allUsers
    .filter((u) => u.id !== currentUser?.id)
    .filter((u) =>
      `${u.first_name} ${u.last_name}`
        .toLowerCase()
        .includes(userSearch.toLowerCase())
    );

  const [formData, setFormData] = useState({
    planned_date: "",
    planned_time: "",
    notes: "",
    dive_type: "",
    gas: "",
    config: "",
    invited_users: [],
    visibility: "public",
    club_id: "",
  });


  const apiUrl = process.env.REACT_APP_API_URL;
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Panneau desktop "Airbnb" : sélectionner un site depuis la liste → voler dessus + fiche dans le panneau.
  const handlePanelSelect = (site) => {
    const [latStr = "", lonStr = ""] = String(site.coordinates || "").split(",");
    const lat = parseFloat(latStr.trim());
    const lon = parseFloat(lonStr.trim());
    const map = mapRef.current;
    if (map && !Number.isNaN(lat) && !Number.isNaN(lon)) {
      map.flyTo([lat, lon], Math.max(map.getZoom() || 9, 12), { duration: 0.6 });
    }
    setPanelSiteId(site.id);
  };

  // Survol d'une ligne du panneau → tooltip + mise en avant du marqueur (impératif).
  const handlePanelHover = (siteId) => {
    const prev = hoveredRef.current;
    if (prev && markerRefs.current[prev]) {
      const pm = markerRefs.current[prev];
      pm.closeTooltip();
      if (pm.setZIndexOffset) pm.setZIndexOffset(0);
    }
    hoveredRef.current = siteId;
    const m = siteId && markerRefs.current[siteId];
    if (m) {
      m.openTooltip();
      if (m.setZIndexOffset) m.setZIndexOffset(2000);
    }
  };

  // Replier/déplier le panneau change la largeur de la carte → Leaflet doit recalculer.
  useEffect(() => {
    const timer = setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 320);
    return () => clearTimeout(timer);
  }, [panelCollapsed]);

  // "Voir sur la carte" depuis le bandeau stories → voler sur le site de la plongée.
  useEffect(() => {
    const onFocusSite = (e) => {
      const siteId = e?.detail?.siteId;
      if (!siteId) return;
      const site = sites.find((s) => s.id === siteId);
      if (!site) return;
      const [latStr = "", lonStr = ""] = String(site.coordinates || "").split(",");
      const lat = parseFloat(latStr.trim());
      const lon = parseFloat(lonStr.trim());
      const map = mapRef.current;
      if (map && !Number.isNaN(lat) && !Number.isNaN(lon)) {
        map.flyTo([lat, lon], Math.max(map.getZoom() || 9, 12), { duration: 0.6 });
      }
      setPanelSiteId(site.id);
    };
    window.addEventListener("sda:focus-site", onFocusSite);
    return () => window.removeEventListener("sda:focus-site", onFocusSite);
  }, [sites]);

  // Récupère le canton du profil pour centrer la carte sur la région de l'utilisateur.
  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    fetchWithAuth(`${apiUrl}/users/${currentUser.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        const center = u?.canton && CANTON_CENTERS[u.canton];
        if (!cancelled && center) setUserCenter(center);
      })
      .catch(() => { /* silencieux : on garde le centrage par défaut */ });
    return () => { cancelled = true; };
  }, [currentUser?.id, apiUrl]);

  // Mes clubs (pour la visibilité "mon club" du formulaire)
  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    fetchWithAuth(`${apiUrl}/my-clubs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setMyClubsForForm(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.id, apiUrl]);

  // Derniers rapports de conditions par site (pour la pastille dans la popup)
  useEffect(() => {
    let cancelled = false;
    fetchWithAuth(`${apiUrl}/site-conditions/latest`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => { if (!cancelled && d && typeof d === "object") setLatestConditions(d); })
      .catch(() => { /* silencieux */ });
    return () => { cancelled = true; };
  }, [apiUrl]);

  // Calculer les invitations en attente
  const pendingInvitations = useMemo(() => {
    if (!currentUser?.id || !Array.isArray(sites)) return [];

    const invitations = [];
    sites.forEach(site => {
      const dives = Array.isArray(site.plannedDives) ? site.plannedDives : [];
      dives.forEach(dive => {
        if (
          Array.isArray(dive.invited_users) &&
          dive.invited_users.includes(currentUser.id) &&
          !dive.joined &&
          dive.user_id !== currentUser.id // pas ses propres plongées
        ) {
          invitations.push({
            ...dive,
            site_name: site.name,
            site_id: site.id
          });
        }
      });
    });
    return invitations;
  }, [sites, currentUser?.id]);

  const reloadSites = async () => {
    const url = currentUser?.id
      ? `${apiUrl}/diving-sites-status?user_id=${currentUser.id}`
      : `${apiUrl}/diving-sites-status`;

    try {
      const r = await fetchWithAuth(url);
      // 401 (session expirée) ou autre erreur → le corps n'est PAS un tableau :
      // on ne met pas un objet d'erreur dans l'état (sinon .find/.map plantent).
      if (!r.ok) return;
      const updated = await r.json();
      if (!Array.isArray(updated)) return;
      setSites(updated);
      setFilteredSites(updated);
    } catch (err) {
      console.error("reloadSites:", err);
    }
  };

  // ---------------- Effets ----------------
  // Fermer modales avec ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowDetailsModal(false);
        setShowModal(false);
        setShowChat(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Charger les sites (+ statut plongées)
  useEffect(() => {
    reloadSites().catch((err) => console.error("Erreur fetch sites:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, apiUrl]);

  // Charger les POIs
  useEffect(() => {
    fetch(`${apiUrl}/points-of-interest`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setPois(data);
        else console.error("Réponse POI inattendue:", data);
      })
      .catch((err) => console.error("Erreur fetch POI:", err));
  }, [apiUrl]);

  // Fermer le dropdown POI quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (poiDropdownRef.current && !poiDropdownRef.current.contains(e.target)) {
        setPoiDropdownOpen(false);
      }
    };
    if (poiDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [poiDropdownOpen]);

  // Appliquer les filtres (date, canton, type, gaz)
  useEffect(() => {
    if (!Array.isArray(sites)) return;

    // Convertit un objet Date ou string en "YYYY-MM-DD" sans fuseau
    const toISODateLocal = (d) => {
      if (!d) return null;
      const date = d instanceof Date ? d : new Date(d);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    const cantonMap = {
      AG: "Argovie", AI: "Appenzell Rhodes-Intérieures", AR: "Appenzell Rhodes-Extérieures",
      BE: "Berne", BL: "Bâle-Campagne", BS: "Bâle-Ville", FR: "Fribourg", GE: "Genève",
      GL: "Glaris", GR: "Grisons", JU: "Jura", LU: "Lucerne", NE: "Neuchâtel",
      NW: "Nidwald", OW: "Obwald", SG: "Saint-Gall", SH: "Schaffhouse", SO: "Soleure",
      SZ: "Schwyz", TG: "Thurgovie", TI: "Tessin", UR: "Uri", VD: "Vaud", VS: "Valais",
      ZG: "Zoug", ZH: "Zürich",
    };

    const todayISO = toISODateLocal(new Date()); // "2025-11-05"
    const selectedDate =
      typeof filterDate === "string" && filterDate.length >= 10
        ? filterDate.slice(0, 10)
        : null;

    const next = sites
      .map((site) => {
        const dives = Array.isArray(site.plannedDives) ? site.plannedDives : [];

        const kept = dives.filter((d) => {
          const planned = (d.planned_date || "").slice(0, 10); // ex: "2025-11-15"

          // Logique corrigée
          // On combine la date et l’heure pour une comparaison plus fine
          const plannedDateTime = new Date(`${d.planned_date}T${d.planned_time || "00:00"}`);
          const now = new Date();

          // Si un filtre date est choisi → on garde ce jour-là uniquement.
          // Sinon on garde les plongées à venir. La marge de 2h après le début
          // (retardataires + accès au chat) ne s'applique QU'AUX inscrits/organisateur :
          // pour les autres, la plongée disparaît à l'heure de début (plus de point
          // "périmé" qu'ils ne peuvent de toute façon plus rejoindre).
          const isMine = d.joined || d.user_id === currentUser?.id;
          const cutoff = isMine ? now.getTime() - 2 * 60 * 60 * 1000 : now.getTime();
          const matchDate = selectedDate
            ? d.planned_date === selectedDate
            : plannedDateTime.getTime() >= cutoff;


          // Type : garde ton comportement actuel (egal = joker)
          const matchType =
            !filterType ||
            (filterType === "egal" ? d.dive_type === "egal" : d.dive_type === filterType);

          // Gaz : "egal" signifie "montrer seulement les plongées marquées Au choix"
          const matchGas =
            !filterGas ||
            (filterGas === "egal" ? d.gas === "egal" : d.gas === filterGas);

          // Configuration : même logique que Gaz
          const matchConfig =
            !filterConfig ||
            (filterConfig === "egal" ? d.config === "egal" : d.config === filterConfig);

          return matchDate && matchType && matchGas && matchConfig;
        });

        return { ...site, plannedDives: kept, hasPlannedDive: kept.length > 0 };
      })
      .filter((site) => {
        const hasFilters =
          selectedDate ||
          filterType ||
          filterGas ||
          filterConfig ||
          filterCanton;

        // Si aucun filtre actif → on garde tout
        if (!hasFilters) return true;

        // Si un filtre actif → on garde seulement les sites avec plongées correspondantes
        if (!site.hasPlannedDive) return false;

        // Filtrage spécifique sur le canton
        if (filterCanton) {
          const cantonName = cantonMap[filterCanton] || filterCanton;
          return site.canton?.toLowerCase() === cantonName.toLowerCase();
        }

        return true;
      });

    setFilteredSites(next);
  }, [sites, filterDate, filterCanton, filterType, filterGas, filterConfig]);

  const [showInviteList, setShowInviteList] = useState(false);

  // Charger les participants quand on sélectionne une plongée
  useEffect(() => {
    if (selectedDive) {
      fetchWithAuth(`${apiUrl}/dive-participants/${selectedDive.id}`)
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) setParticipants(data);
          else setParticipants([]);
        })
        .catch((err) =>
          console.error("Erreur chargement participants :", err)
        );
    } else {
      setParticipants([]);
    }
  }, [selectedDive, apiUrl]);

  useEffect(() => {
    fetchWithAuth(`${apiUrl}/users`)
      .then((res) => res.json())
      .then(setAllUsers)
      .catch((err) => console.error("Erreur chargement utilisateurs :", err));
  }, [apiUrl]);

  useEffect(() => {
    window.closeChat = () => setShowChat(false);
    return () => { delete window.closeChat };
  }, []);

  // ---------------- Handlers ----------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser?.id || !selectedSite?.id) {
      toast.error(t('map.toasts.mustBeLoggedIn'));
      return;
    }

    const payload = {
      site_id: selectedSite.id,
      planned_date: formData.planned_date,
      planned_time: formData.planned_time,
      notes: formData.notes,
      dive_type: formData.dive_type,
      gas: formData.gas,
      config: formData.config,
      invited_users: formData.invited_users || [],
      visibility: formData.visibility || "public",
      club_id: formData.visibility === "club" ? (formData.club_id || null) : null,
    };

    if (payload.visibility === "club" && !payload.club_id) {
      toast.error(t('diveForm.clubRequired'));
      return;
    }

    const method = editingDive ? "PUT" : "POST";
    const url = editingDive
      ? `${apiUrl}/dive-intentions/${editingDive.id}`
      : `${apiUrl}/dive-intentions`;

    try {
      const res = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('map.toasts.saveError'));

      toast.success(editingDive ? t('map.toasts.diveUpdated') : t('map.toasts.divePlanned'));
      setShowModal(false);
      setEditingDive(null);
      setFormData({
        planned_date: "",
        planned_time: "",
        notes: "",
        dive_type: "",
        gas: "",
        config: "",
      });

      // Recharger les sites
      const urlSites = currentUser?.id
        ? `${apiUrl}/diving-sites-status?user_id=${currentUser.id}`
        : `${apiUrl}/diving-sites-status`;
      const updated = await fetchWithAuth(urlSites).then((r) => r.json());
      setSites(updated);
      setFilteredSites(updated);
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleCancelDive = async (diveId) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/dive-intentions/${diveId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(t('map.toasts.cancelError'));
      toast.info(t('map.toasts.diveCancelled'));
      setSites((prev) =>
        prev.map((s) => {
          const kept = (s.plannedDives || []).filter((d) => d.id !== diveId);
          return { ...s, plannedDives: kept, hasPlannedDive: kept.length > 0 };
        })
      );
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleJoinDive = async (diveId) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/join-dive`, {
        method: "POST",
        body: JSON.stringify({
          dive_intention_id: diveId,
        }),
      });
      if (!res.ok) {
        const eJson = await res.json().catch(() => ({}));
        throw new Error(eJson?.error || t('map.toasts.joinError'));
      }
      toast.success(t('map.toasts.diveJoined'));
      await reloadSites();
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleUnsubscribe = async (diveId) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/unsubscribe-dive`, {
        method: "POST",
        body: JSON.stringify({
          dive_intention_id: diveId,
        }),
      });
      if (!res.ok) {
        const eJson = await res.json().catch(() => ({}));
        throw new Error(eJson?.error || t('map.toasts.unsubscribeError'));
      }
      toast.info(t('map.toasts.diveUnsubscribed'));
      await reloadSites();
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  // Accepter une invitation = rejoindre la plongée
  const handleAcceptInvitation = async (invitation) => {
    await handleJoinDive(invitation.id);
  };

  // Refuser une invitation (retire l'utilisateur de la liste invited_users)
  const handleDeclineInvitation = async (invitation) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/decline-invitation`, {
        method: "POST",
        body: JSON.stringify({
          dive_intention_id: invitation.id,
        }),
      });
      if (!res.ok) {
        const eJson = await res.json().catch(() => ({}));
        throw new Error(eJson?.error || "Erreur lors du refus");
      }
      toast.info(t('invitations.declined'));
      await reloadSites();
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleEditDive = async (dive, site) => {
    setEditingDive(dive);
    setSelectedSite(site);
    setShowModal(true);

    try {
      const res = await fetchWithAuth(`${apiUrl}/dive-invitations/${dive.id}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setFormData((prev) => ({
          ...prev,
          invited_users: data.map((u) => u.id),
        }));
      }
    } catch (err) {
      console.error("Erreur chargement invitations :", err);
    }
  };

  useEffect(() => {
    if (editingDive) {
      setFormData({
        planned_date: editingDive.planned_date || "",
        planned_time: editingDive.planned_time || "",
        notes: editingDive.notes || "",
        dive_type: editingDive.dive_type || "",
        gas: editingDive.gas || "",
        config: editingDive.config || "",
        invited_users: editingDive.invited_users || [],
        visibility: editingDive.visibility || "public",
        club_id: editingDive.club_id || "",
      });
    }
  }, [editingDive]);


  useEffect(() => {
    // À chaque (ré)ouverture du formulaire, on referme le sélecteur "changer de site"
    setChangingSite(false);
    setSiteSearch("");
    if (showModal && !editingDive) {
      // Nouvelle plongée → réinitialise les champs
      setFormData({
        planned_date: "",
        planned_time: "",
        notes: "",
        dive_type: "",
        gas: "",
        config: "",
        invited_users: [],
        visibility: "public",
        club_id: "",
      });
    }
  }, [showModal, editingDive]);

  const swissBounds = useMemo(() => ([
    [45.8, 5.9],
    [47.9, 10.6],
  ]), []);

  const swissMaxBounds = useMemo(() => ([
    [44.0, 3.0],
    [50.0, 14.0],
  ]), []);

  const [isMobile, setIsMobile] = useState(window.matchMedia("(max-width: 600px)").matches);
  const [isDesktop, setIsDesktop] = useState(window.matchMedia("(min-width: 1024px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 600px)");
    const handler = (e) => setIsMobile(e.matches);

    // compat
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Desktop (>=1024) : le panneau latéral existe → un clic sur un marqueur ouvre
  // la fiche dans le panneau plutôt que la popup Leaflet.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  const isAnyModalOpen = showModal || showChat || showParticipants || showSiteDetails;
  // ---------------- Render ----------------
  return (
    <div className={`map-page ${isPopupOpen ? "popup-open" : ""}`}>

      <div className={`map-layout ${panelCollapsed ? "panel-collapsed" : ""}`}>
        <MapSidePanel
          sites={filteredSites}
          activeSite={(Array.isArray(filteredSites) ? filteredSites : []).find((s) => s.id === panelSiteId) || null}
          currentUser={currentUser}
          latestConditions={latestConditions}
          invitations={pendingInvitations}
          onAccept={handleAcceptInvitation}
          onDecline={handleDeclineInvitation}
          onHover={handlePanelHover}
          onSelect={handlePanelSelect}
          onBack={() => {
            setPanelSiteId(null);
            const map = mapRef.current;
            if (map) {
              if (userCenter) map.flyTo(userCenter, CANTON_ZOOM, { duration: 0.6 });
              else map.flyToBounds(swissBounds, { padding: [60, 60], maxZoom: 10, duration: 0.6 });
            }
          }}
          onCollapse={() => setPanelCollapsed(true)}
          onPlan={(site) => { setEditingDive(null); setSelectedSite(site); setShowModal(true); }}
          onConditions={(site) => { setSelectedSite(site); setShowSiteDetails(true); }}
          onJoin={handleJoinDive}
          onUnsubscribe={handleUnsubscribe}
          onChat={(dive) => { setSelectedDive(dive); setShowChat(true); }}
          onParticipants={(dive) => { setSelectedDive(dive); setShowParticipants(true); }}
          onEdit={(dive, site) => handleEditDive(dive, site)}
        />
        <div className="map-canvas">
          {/* Bouton pour rouvrir le panneau (visible seulement quand il est replié, desktop) */}
          <button
            type="button"
            className="map-panel-reopen"
            onClick={() => setPanelCollapsed(false)}
            aria-label={t('mapPanel.title')}
            title={t('mapPanel.title')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
      <MapContainer
        ref={mapRef}
        minZoom={7}
        maxBounds={swissMaxBounds}
        maxBoundsViscosity={0.7}
        style={{ height: "100%", width: "100%", position: "relative" }}
      >
        <SwissView isMobile={isMobile} bounds={swissBounds} userCenter={userCenter} />

        {/* Bouton POI sur la carte avec dropdown filtres */}
        {!isAnyModalOpen && !isPopupOpen && (
          <div style={{ position: "absolute", top: isMobile ? 10 : 54, right: 30, zIndex: 800 }}>
            <div ref={poiDropdownRef} style={{ position: "relative" }}>
              <button
                onClick={() => {
                  if (!showPois) {
                    setShowPois(true);
                    setPoiDropdownOpen(true);
                  } else {
                    setPoiDropdownOpen((v) => !v);
                  }
                }}
                style={{
                  background: showPois ? "var(--primary-blue)" : "white",
                  color: showPois ? "white" : "#333",
                  border: "1px solid #ccc",
                  borderRadius: 8,
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 2 L3 6 v14 a2 2 0 0 0 2 2 h14 a2 2 0 0 0 2 -2 V6 l-3 -4 z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10 a4 4 0 0 1 -8 0" />
                </svg>
                {!isMobile && <span>{t('map.poi.shopsAndServices')}</span>}
                <span style={{ fontSize: "10px" }}>{poiDropdownOpen ? "▲" : "▼"}</span>
              </button>

              {/* Dropdown menu */}
              {poiDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "4px",
                    background: "white",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    minWidth: "180px",
                    padding: "8px 0",
                    zIndex: 900,
                  }}
                >
                  {/* Option Masquer */}
                  {showPois && (
                    <button
                      onClick={() => {
                        setShowPois(false);
                        setPoiDropdownOpen(false);
                        setPoiFilters([]);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "8px 14px",
                        background: "none",
                        border: "none",
                        textAlign: "left",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: "#c00",
                        fontWeight: 500,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      {t('map.poi.hideShops')}
                    </button>
                  )}

                  {/* Filtres */}
                  {[
                    { key: "all", label: t('map.poi.filters.all') },
                    { key: "gonflage", label: t('map.poi.filters.airFill') },
                    { key: "location", label: t('map.poi.filters.rental') },
                    { key: "atelier", label: t('map.poi.filters.workshop') },
                    { key: "formation", label: t('map.poi.filters.training') },
                  ].map(({ key, label }) => {
                    const isAll = key === "all";
                    const isChecked = isAll ? poiFilters.length === 0 : poiFilters.includes(key);

                    return (
                      <label
                        key={key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 14px",
                          cursor: "pointer",
                          fontSize: "13px",
                          color: "#333",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (isAll) {
                              setPoiFilters([]);
                            } else {
                              setPoiFilters((prev) =>
                                prev.includes(key)
                                  ? prev.filter((f) => f !== key)
                                  : [...prev, key]
                              );
                            }
                          }}
                          style={{ accentColor: "var(--primary-blue)" }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <SwissMask />

        {Array.isArray(filteredSites) &&
          filteredSites.map((site) => {
            const [latStr = "", lonStr = ""] = String(site.coordinates || "").split(",");
            const lat = parseFloat(latStr.trim());
            const lon = parseFloat(lonStr.trim());
            if (Number.isNaN(lat) || Number.isNaN(lon)) return null;

            const userJoined = site.plannedDives?.some(
              (d) => d.joined || d.user_id === currentUser?.id
            );

            const icon = userJoined ? greenIcon : site.hasPlannedDive ? blueIcon : redIcon;

            // Calcul nb. de plongées
            const diveCount = site.plannedDives?.length || 0;
            const tooltipText =
              diveCount > 0
                ? `${site.name} – ${t('map.tooltip.upcomingDives', { count: diveCount })}`
                : site.name;

            return (
              <Marker
                key={site.id}
                ref={(m) => { if (m) markerRefs.current[site.id] = m; }}
                position={[lat, lon]}
                icon={icon}
                zIndexOffset={site.hasPlannedDive ? 1000 : 0}
                eventHandlers={{ click: () => { if (isDesktop) setPanelSiteId(site.id); } }}
              >
                <Tooltip direction="top" offset={[0, -8]} opacity={0.9}>
                  {tooltipText}
                </Tooltip>
                {!isDesktop && (
                <Popup
                  maxWidth={330}
                  eventHandlers={{
                    add: () => setIsPopupOpen(true),
                    remove: () => setIsPopupOpen(false),
                  }}
                >
                  <div
                    style={{
                      minWidth: "280px",
                      maxHeight: "min(70vh, 560px)", // hauteur adaptée à l'écran (plus de scroll interne dans le cas courant)
                      overflowY: "auto",             // défilement interne seulement si vraiment beaucoup de contenu
                      padding: "10px",
                      fontFamily: "Inter, Arial, sans-serif",
                      color: "#333",
                    }}
                  >
                    {/* Nom du site + bouton Détails */}
                    <h3
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "16px",
                        color: "var(--primary-blue)",
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      {site.name}
                      {(
                        <button
                          onClick={() => {
                            setSelectedSite(site);
                            setShowSiteDetails(true);
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--primary-blue)",
                            padding: 0,
                            margin: 0,
                            lineHeight: 0,
                            transition: "transform 0.2s ease, color 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "var(--primary-blue-dark)";
                            e.currentTarget.style.transform = "scale(1.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "var(--primary-blue)";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          aria-label={t('map.siteDetails.showDetails')}
                          title={t('map.siteDetails.showDetails')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                        </button>
                      )}
                    </h3>

                    {/* Pastille "conditions récentes" (signal + fraîcheur, sans note unique trompeuse) */}
                    {(() => {
                      const lc = latestConditions[site.id];
                      if (!lc) return null;
                      const recent = daysSince(lc.observed_date) <= 14;
                      return (
                        <button
                          onClick={() => { setSelectedSite(site); setShowSiteDetails(true); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 8, width: "100%",
                            background: "#f7fbfd", border: "1px solid #e6edf3", borderRadius: 8,
                            padding: "6px 10px", marginBottom: 10, cursor: "pointer",
                            fontSize: 12.5, color: "#556",
                          }}
                          title={t('conditions.latestTitle')}
                        >
                          <span style={{ width: 10, height: 10, borderRadius: "50%", background: recent ? "#2e9e5b" : "#b9c3cc", flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: "#223344" }}>{t('conditions.seeConditions')}</span>
                          <span style={{ marginLeft: "auto", color: "#8a97a3" }}>{relativeDays(lc.observed_date, i18n.language)}</span>
                        </button>
                      );
                    })()}

                    {/* Bouton Planifier */}
                    <button
                      onClick={() => {
                        // IMPORTANT : forcer un "mode création"
                        setEditingDive(null);
                        setSelectedSite(site);
                        setShowModal(true);
                      }}
                      style={{
                        width: "100%",
                        background: "var(--primary-blue)",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        marginBottom: "10px",
                      }}
                    >
                      {t('map.planDive')}
                    </button>

                    {/* Plongées planifiées */}
                    {site.hasPlannedDive && (
                      <>
                        <h4
                          style={{
                            margin: "10px 0 6px",
                            color: "var(--primary-blue)",
                            fontSize: "14px",
                            borderBottom: "1px solid #ddd",
                            paddingBottom: "4px",
                          }}
                        >
                          {t('map.plannedDives')}
                        </h4>

                        <ul style={{ paddingLeft: 0, listStyle: "none" }}>
                          {site.plannedDives.map((dive) => (
                            <li
                              key={dive.id}
                              style={{
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                padding: "10px",
                                marginBottom: "8px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                position: "relative",
                              }}
                            >
                              {/* --- Icône de modification --- */}
                              {currentUser?.id === dive.user_id && (
                                <button
                                  onClick={() => handleEditDive(dive, site)}
                                  style={{
                                    position: "absolute",
                                    top: "4px",
                                    right: "4px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "50%",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "var(--primary-blue)",
                                    lineHeight: 0,
                                    transition: "background 0.2s ease, color 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(0,119,182,0.12)";
                                    e.currentTarget.style.color = "var(--primary-blue-dark)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.color = "var(--primary-blue)";
                                  }}
                                  aria-label={t('map.editDive')}
                                  title={t('map.editDive')}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                  </svg>
                                </button>
                              )}

                              <div
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "4px",
                                }}
                              >
                                <UserLink id={dive.user?.id || dive.user_id} firstName={dive.user?.first_name} lastName={dive.user?.last_name} fallback={t('map.firstName')} />
                              </div>

                              {(() => {
                                const v = diveVisibilityMeta(dive.visibility);
                                return (
                                  <div style={{ fontSize: "12px", color: "#777", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: v.color, flexShrink: 0 }} />
                                    {t(`map.diveCard.visibilityLabel.${v.key}`)}
                                  </div>
                                );
                              })()}

                              <div style={{ fontSize: "13px", color: "#555" }}>
                                {dive.planned_date} &nbsp; {dive.planned_time}
                                <br />
                                {t('map.diveCard.type')} {dive.dive_type ? t(`diveTypes.${dive.dive_type}`) : "—"}
                                <br />
                                {t('map.diveCard.gas')}{" "}
                                {dive.gas ? t(`gasTypes.${dive.gas}`) : "—"}
                                <br />
                                {t('map.diveCard.config')}{" "}
                                {dive.config ? t(`configTypes.${dive.config}`) : "—"}
                              </div>

                              {dive.notes && (
                                <div
                                  style={{
                                    marginTop: "5px",
                                    fontSize: "13px",
                                    fontStyle: "italic",
                                    color: "#666",
                                  }}
                                >
                                  {dive.notes}
                                </div>
                              )}

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "6px",
                                  marginTop: "10px",
                                }}
                              >
                                {/* Boutons selon statut */}
                                {currentUser?.id === dive.user_id ? (
                                  <>
                                    <button
                                      onClick={() => {
                                        setSelectedDive(dive);
                                        setShowChat(true);
                                      }}
                                      style={{
                                        flex: 1,
                                        background: "var(--primary-blue)",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        padding: "6px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {t('map.buttons.chat')}
                                    </button>
                                    <button
                                      onClick={() => handleCancelDive(dive.id)}
                                      style={{
                                        flex: 1,
                                        background: "#e63946",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        padding: "6px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {t('map.buttons.delete')}
                                    </button>
                                  </>
                                ) : dive.joined ? (
                                  <>
                                    <button
                                      onClick={() => {
                                        setSelectedDive(dive);
                                        setShowChat(true);
                                      }}
                                      style={{
                                        flex: 1,
                                        background: "var(--primary-blue)",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        padding: "6px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {t('map.buttons.chat')}
                                    </button>
                                    <button
                                      onClick={() => handleUnsubscribe(dive.id)}
                                      style={{
                                        flex: 1,
                                        background: "#f77f00",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        padding: "6px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {t('map.buttons.unsubscribe')}
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => handleJoinDive(dive.id)}
                                    style={{
                                      flex: 1,
                                      background: "var(--primary-blue)",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      padding: "6px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {t('map.buttons.join')}
                                  </button>
                                )}

                                <button
                                  onClick={() => {
                                    setSelectedDive(dive);
                                    setShowParticipants(true);
                                  }}
                                  style={{
                                    flexBasis: "100%",
                                    background: "#e3f2fd",
                                    border: "1px solid var(--primary-blue)",
                                    color: "var(--primary-blue)",
                                    borderRadius: "6px",
                                    padding: "6px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                  }}
                                >
                                  {t('map.buttons.viewParticipants')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </Popup>
                )}
              </Marker>
            );

          }

          )}
        {showPois && pois
          .filter((p) => {
            // Si aucun filtre sélectionné, afficher tous
            if (poiFilters.length === 0) return true;

            const services = String(p.services || "").toLowerCase();
            const hasGonflage = p.has_air_fill || services.includes("gonflage");
            const hasLocation = services.includes("location");
            const hasAtelier = services.includes("atelier") || services.includes("réparation");
            const hasFormation = services.includes("formation");

            // OU logique : afficher si au moins un des filtres correspond
            return poiFilters.some((f) => {
              if (f === "gonflage") return hasGonflage;
              if (f === "location") return hasLocation;
              if (f === "atelier") return hasAtelier;
              if (f === "formation") return hasFormation;
              return false;
            });
          })
          .map((p) => {
          if (!p.coordinates) return null;

          const [latStr, lngStr] = String(p.coordinates).split(",").map(s => s.trim());
          const lat = parseFloat(latStr);
          const lng = parseFloat(lngStr);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

          return (
            <Marker
              key={p.id}
              position={[lat, lng]}
              icon={poiIcon}
              eventHandlers={{
                click: (e) => {
                  // Ferme le tooltip quand on clique
                  e.target.closeTooltip();
                },
              }}
            >
              <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                {p.name}
              </Tooltip>

              <Popup
                eventHandlers={{
                  add: () => setIsPopupOpen(true),
                  remove: () => setIsPopupOpen(false),
                }}
              >
                <div className="poiPopup">
                  <div className="poiTitle">{p.name}</div>

                  {/* Services en "chips" (lisible) */}
                  {(p.type || p.services) && (
                    <div className="poiChips">
                      {/* Chip "Magasin" selon le type */}
                      {String(p.type || "").trim().toLowerCase() === "shop" && (
                        <span className="poiChip">{t('map.poi.shop')}</span>
                      )}

                      {/* Services chips */}
                      {String(p.services || "")
                        .split(/[,;]\s*|\n+/)
                        .map(s => s.trim())
                        .filter(Boolean)
                        .map(s => (
                          <span key={s} className="poiChip">{s}</span>
                        ))}
                    </div>
                  )}

                  <div className="poiKV">

                    {/* Gaz */}
                    {p.has_air_fill && p.fill_gases && (
                      <>
                        <div className="poiK">{t('map.poi.airFill')}</div>
                        <div className="poiV">
                          <div className="poiChips">
                            {String(p.fill_gases)
                              .split(/[,;]\s*|\n+/)
                              .map(s => s.trim())
                              .filter(Boolean)
                              .map(g => (
                                <span key={g} className="poiChip poiChip--gas">{g}</span>
                              ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Conditions (à la place de "Détails gonflage") */}
                    {p.has_air_fill && p.fill_details && (
                      <>
                        <div className="poiK">{t('map.poi.conditions')}</div>
                        <div className="poiV" style={{ whiteSpace: "pre-wrap" }}>
                          {p.fill_details}
                        </div>
                      </>
                    )}

                    {p.phone && (
                      <>
                        <div className="poiK">{t('map.poi.phone')}</div>
                        <div className="poiV">
                          <a href={`tel:${String(p.phone).replace(/[^\d+]/g, "")}`}>{p.phone}</a>
                        </div>
                      </>
                    )}

                    {p.website && (
                      <>
                        <div className="poiK">{t('map.poi.website')}</div>
                        <div className="poiV">
                          <a href={p.website} target="_blank" rel="noopener noreferrer">
                            {(() => {
                              try { return new URL(p.website).hostname.replace(/^www\./, ""); }
                              catch { return t('map.poi.open'); }
                            })()}
                          </a>
                        </div>
                      </>
                    )}

                  </div>
                </div>
              </Popup>

            </Marker>
          );
        })}

      </MapContainer>

      {/* Légende compacte — masquée quand une modale/popup est ouverte */}
      {!isAnyModalOpen && !isPopupOpen && (
        <div className="map-legend" aria-hidden="true">
          <div className="row"><span className="legend-dot red"></span>{t('header.legend.noDiveShort')}</div>
          <div className="row"><span className="legend-dot blue"></span>{t('header.legend.plannedShort')}</div>
          <div className="row"><span className="legend-dot green"></span>{t('header.legend.registeredShort')}</div>
        </div>
      )}
        </div>{/* .map-canvas */}
      </div>{/* .map-layout */}

      {/* Création rapide : recherche d'un site */}
      {showCreateSearch && (
        <div
          onClick={() => setShowCreateSearch(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            zIndex: 10002, padding: "72px 16px 16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "14px", width: "100%", maxWidth: "440px",
              maxHeight: "calc(100dvh - 100px)", display: "flex", flexDirection: "column",
              boxShadow: "0 12px 40px rgba(0,0,0,0.25)", overflow: "hidden",
            }}
          >
            <div style={{ padding: "16px 18px 10px" }}>
              <h3 style={{ margin: "0 0 10px", color: "var(--primary-blue)", fontSize: "18px" }}>
                {t('create.title')}
              </h3>
              <input
                autoFocus
                type="text"
                placeholder={t('create.searchPlaceholder')}
                value={createSearch}
                onChange={(e) => setCreateSearch(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", fontSize: "15px",
                  border: "1px solid #ccc", borderRadius: "9px", outline: "none", boxSizing: "border-box",
                }}
              />
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: "0 8px 10px", overflowY: "auto" }}>
              {(() => {
                const term = createSearch.trim().toLowerCase();
                const list = (Array.isArray(sites) ? sites : [])
                  .filter((s) => s?.name && (!term || s.name.toLowerCase().includes(term)))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .slice(0, 60);
                if (list.length === 0) {
                  return <li style={{ padding: "14px", color: "#777", textAlign: "center" }}>{t('create.noResults')}</li>;
                }
                return list.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => openPlanForSite(s)}
                      style={{
                        width: "100%", textAlign: "left", background: "none", border: "none",
                        borderBottom: "1px solid #f0f0f0", padding: "11px 10px", cursor: "pointer",
                        fontSize: "15px", color: "#223344", display: "flex",
                        justifyContent: "space-between", gap: "10px", alignItems: "center",
                      }}
                    >
                      <span>{s.name}</span>
                      {s.canton && <span style={{ color: "#8aa0a8", fontSize: "13px", flexShrink: 0 }}>{s.canton}</span>}
                    </button>
                  </li>
                ));
              })()}
            </ul>
            <div style={{ padding: "10px 12px 4px", borderTop: "1px solid #eee", textAlign: "center" }}>
              <button
                type="button"
                onClick={() => { setShowCreateSearch(false); navigate("/about?tab=suggest"); }}
                style={{ background: "none", border: "none", color: "var(--primary-blue)", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
              >
                {t('create.suggestSite')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invitations : desktop = panneau latéral gauche (MapSidePanel) ; mobile =
          section en haut de l'onglet Plongées + pastille sur la BottomNav
          (plus de bouton flottant sur la carte). */}

      {/* Flou doux seulement hors Suisse */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at center, rgba(255,255,255,0) 50%, rgba(255,255,255,0.6) 100%)",
          pointerEvents: "none",
          zIndex: 450,
        }}
      />

      {/* ----- Modal Planifier ----- */}
      {showModal && selectedSite && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 10000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              padding: 20,
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: 8,
              width: 480,
              maxWidth: "95vw",
              maxHeight: "calc(100dvh - 24px)",
              overflowY: "auto",
              zIndex: 10001,
            }}
          >
            {/* ---- Titre + bouton fermer ---- */}
            <h2 style={{ marginTop: 0, color: "var(--primary-blue)", position: "relative" }}>
              {selectedSite.name}
              <p style={{ marginTop: 0, color: "#666", fontSize: 13 }}>{t('map.modal.divePlanning')}</p>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  color: "#999",
                  cursor: "pointer",
                  padding: "5px 8px",
                  lineHeight: "1",
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#333")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#999")}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </h2>

            {/* ---- Formulaire ---- */}
            <form onSubmit={handleSubmit}>
              {/* Site (changement possible en édition uniquement) */}
              {editingDive && (
                <div style={{ display: "flex", alignItems: changingSite ? "flex-start" : "center", marginBottom: 10, gap: 10 }}>
                  <label style={{ width: 150, paddingTop: changingSite ? 8 : 0 }}>{t('diveForm.labels.site')}</label>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {!changingSite ? (
                      <button
                        type="button"
                        onClick={() => { setChangingSite(true); setSiteSearch(""); }}
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          borderRadius: 6,
                          border: "1px solid #ccc",
                          padding: 6,
                          background: "#fff",
                          cursor: "pointer",
                          fontSize: 14,
                          color: "#223344",
                        }}
                        title={t('map.modal.changeSite')}
                      >
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedSite.name}</span>
                        <span style={{ color: "#8aa0a8", flexShrink: 0, fontSize: 12 }}>▾</span>
                      </button>
                    ) : (
                      <div>
                        <input
                          autoFocus
                          type="text"
                          placeholder={t('create.searchPlaceholder')}
                          value={siteSearch}
                          onChange={(e) => setSiteSearch(e.target.value)}
                          style={{ width: "100%", padding: 6, fontSize: 14, border: "1px solid var(--primary-blue)", borderRadius: 6, outline: "none", boxSizing: "border-box" }}
                        />
                        <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0, maxHeight: 180, overflowY: "auto", border: "1px solid #eef2f4", borderRadius: 8 }}>
                          {(() => {
                            const term = siteSearch.trim().toLowerCase();
                            const list = (Array.isArray(sites) ? sites : [])
                              .filter((s) => s?.name && (!term || s.name.toLowerCase().includes(term)))
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .slice(0, 40);
                            if (list.length === 0) {
                              return <li style={{ padding: 12, color: "#777", textAlign: "center", fontSize: 13 }}>{t('create.noResults')}</li>;
                            }
                            return list.map((s) => (
                              <li key={s.id}>
                                <button
                                  type="button"
                                  onClick={() => { setSelectedSite(s); setChangingSite(false); setSiteSearch(""); }}
                                  style={{
                                    width: "100%",
                                    textAlign: "left",
                                    background: s.id === selectedSite.id ? "rgba(0,150,199,0.10)" : "#fff",
                                    border: "none",
                                    borderBottom: "1px solid #f0f0f0",
                                    padding: "9px 8px",
                                    cursor: "pointer",
                                    fontSize: 14,
                                    color: "#223344",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    alignItems: "center",
                                  }}
                                >
                                  <span>{s.name}{s.id === selectedSite.id ? " ✓" : ""}</span>
                                  {s.canton && <span style={{ color: "#8aa0a8", fontSize: 12, flexShrink: 0 }}>{s.canton}</span>}
                                </button>
                              </li>
                            ));
                          })()}
                        </ul>
                        <button
                          type="button"
                          onClick={() => { setChangingSite(false); setSiteSearch(""); }}
                          style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 12, marginTop: 4, padding: 0 }}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Date */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150 }}>{t('diveForm.labels.date')}</label>
                <input
                  type="date"
                  name="planned_date"
                  value={formData.planned_date}
                  onChange={handleInputChange}
                  required
                  min={today}
                  style={{ flex: 1, borderRadius: 6, border: "1px solid #ccc", padding: 6 }}
                />
              </div>

              {/* Heure */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150 }}>{t('diveForm.labels.time')}</label>
                <input
                  type="time"
                  name="planned_time"
                  value={formData.planned_time}
                  onChange={handleInputChange}
                  required
                  style={{ flex: 1, borderRadius: 6, border: "1px solid #ccc", padding: 6 }}
                />
              </div>

              {/* Type de plongée */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150 }}>{t('diveForm.labels.diveType')}</label>
                <select
                  name="dive_type"
                  value={formData.dive_type}
                  onChange={handleInputChange}
                  required
                  style={{ flex: 1, borderRadius: 6, border: "1px solid #ccc", padding: 6 }}
                >
                  <option value="">{t('diveForm.select')}</option>
                  <option value="egal">{t('diveTypes.egal')}</option>
                  <option value="loisir">{t('diveTypes.loisir')}</option>
                  <option value="deco">{t('diveTypes.deco')}</option>
                </select>
              </div>

              {/* Configuration */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150 }}>{t('diveForm.labels.config')}</label>
                <select
                  name="config"
                  value={formData.config}
                  onChange={handleInputChange}
                  required
                  style={{ flex: 1, borderRadius: 6, border: "1px solid #ccc", padding: 6 }}
                >
                  <option value="">{t('diveForm.select')}</option>
                  <option value="egal">{t('configTypes.egal')}</option>
                  <option value="oc">{t('configTypes.oc')}</option>
                  <option value="cc">{t('configTypes.cc')}</option>
                </select>
              </div>

              {/* Gaz */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150 }}>{t('diveForm.labels.gas')}</label>
                <select
                  name="gas"
                  value={formData.gas}
                  onChange={handleInputChange}
                  required
                  style={{ flex: 1, borderRadius: 6, border: "1px solid #ccc", padding: 6 }}
                >
                  <option value="">{t('diveForm.select')}</option>
                  <option value="egal">{t('gasTypes.egal')}</option>
                  <option value="air">{t('gasTypes.air')}</option>
                  <option value="nx">{t('gasTypes.nx')}</option>
                  <option value="tx">{t('gasTypes.tx')}</option>
                </select>
              </div>

              {/* Notes */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150 }}>{t('diveForm.labels.notes')}</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="1"
                  placeholder={t('diveForm.placeholders.notes')}
                  style={{
                    flex: 1,
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    padding: 6,
                    fontSize: 14,
                  }}
                />
              </div>

              {/* Affichage */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150 }}>{t('diveForm.labels.visibility')}</label>

                <select
                  name="visibility"
                  value={formData.visibility || "public"}
                  onChange={handleInputChange}
                  style={{
                    flex: 1,
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    padding: 6,
                  }}
                >
                  <option value="public">{t('diveForm.visibility.public')}</option>
                  <option value="friends">{t('diveForm.visibility.friends')}</option>
                  {myClubsForForm.length > 0 && <option value="club">{t('diveForm.visibility.club')}</option>}
                  <option value="private">{t('diveForm.visibility.private')}</option>
                </select>
              </div>

              {/* Sélecteur de club (visibilité "mon club") */}
              {formData.visibility === "club" && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
                  <label style={{ width: 150 }}>{t('diveForm.labels.club')}</label>
                  <select
                    name="club_id"
                    value={formData.club_id || ""}
                    onChange={handleInputChange}
                    style={{ flex: 1, width: "100%", minWidth: 0, boxSizing: "border-box", borderRadius: 6, border: "1px solid #ccc", padding: 6 }}
                  >
                    <option value="">{t('diveForm.select')}</option>
                    {myClubsForForm.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}


              {/* Inviter des participants (menu déroulant) */}
              <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 10, gap: 10 }}>
                <label style={{ width: 150, paddingTop: 6 }}>{t('diveForm.labels.invitations')}</label>
                <div style={{ flex: 1 }}>
                  <div
                    onClick={() => setShowInviteList((prev) => !prev)}
                    style={{
                      borderRadius: 6,
                      border: "1px solid #ccc",
                      padding: 8,
                      cursor: "pointer",
                      background: "#f8f9fa",
                      color: "#333",
                      fontSize: 14,
                    }}
                  >
                    {formData.invited_users?.length > 0
                      ? t('diveForm.invitations.selected', { count: formData.invited_users.length })
                      : t('diveForm.invitations.clickToInvite')}
                  </div>

                  {showInviteList && (
                    <div style={{ marginTop: 8, border: "1px solid #ccc", borderRadius: 8, background: "#fff", padding: 8, maxHeight: 180, overflowY: "auto" }}>
                      {/* Champ de recherche */}
                      <input
                        type="text"
                        placeholder={t('diveForm.invitations.searchPlaceholder')}
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        style={{
                          width: "100%",
                          borderRadius: 6,
                          border: "1px solid #ccc",
                          padding: "6px 8px",
                          marginBottom: 8,
                          fontSize: 14,
                        }}
                      />

                      {/* Liste filtrée */}
                      {filteredUsers.length === 0 ? (
                        <p style={{ fontSize: 13, color: "#777", textAlign: "center" }}>{t('diveForm.invitations.noMatch')}</p>
                      ) : (
                        filteredUsers.map((user) => {
                          const selected = formData.invited_users?.includes(user.id);
                          return (
                            <label
                              key={user.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 14,
                                marginBottom: 4,
                                padding: "6px 8px",
                                borderRadius: 6,
                                cursor: "pointer",
                                background: selected ? "#e3f2fd" : "transparent",
                                border: selected ? "1px solid var(--primary-blue)" : "1px solid transparent",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selected || false}
                                onChange={(e) => {
                                  const updated = e.target.checked
                                    ? [...(formData.invited_users || []), user.id]
                                    : (formData.invited_users || []).filter((id) => id !== user.id);
                                  setFormData((prev) => ({ ...prev, invited_users: updated }));
                                }}
                              />
                              <span>
                                {user.first_name} {user.last_name}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Bouton principal */}
              <div style={{ textAlign: "center" }}>
                <button
                  type="submit"
                  style={{
                    borderRadius: 6,
                    border: "none",
                    padding: "8px 16px",
                    background: "var(--primary-blue)",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  {t('map.buttons.plan')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ----- Modal Chat ----- */}
      {showChat && selectedDive && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10002 }}
          onClick={() => setShowChat(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              padding: 0, // on supprime le padding ici, c’est ChatBox qui gère l’intérieur
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: 8,
              width: 520,
              maxWidth: "95vw",
              overflow: "hidden", // pour bien arrondir les bords du ChatBox
            }}
          >
            <ChatBox diveIntentionId={selectedDive.id} currentUser={currentUser} organizer={selectedDive.user} />
          </div>
        </div>
      )}

      {/* ----- Modale Participants (avec animation fade-in) ----- */}
      {showParticipants && selectedDive && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            animation: "fadeIn 0.3s ease-in-out",
          }}
          onClick={() => setShowParticipants(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "25px 30px",
              width: "90%",
              maxWidth: "400px",
              maxHeight: "calc(100dvh - 24px)",
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              fontFamily: "Inter, Arial, sans-serif",
              textAlign: "center",
              color: "#333",
              animation: "slideUp 0.35s ease-out",
            }}
          >
            <h3
              style={{
                color: "var(--primary-blue)",
                fontSize: "18px",
                marginBottom: "15px",
                borderBottom: "2px solid #caf0f8",
                paddingBottom: "6px",
              }}
            >
              {t('map.participants.title')}
            </h3>

            {(() => {
              // Organisateur : déjà présent dans selectedDive.user (car tu le récupères via /diving-sites-status)
              const organizer = selectedDive?.user
                ? { user_id: selectedDive.user_id, users: selectedDive.user }
                : null;

              // Participants venant de /dive-participants/:id
              const apiParticipants = Array.isArray(participants) ? participants : [];

              // Fusion + dédoublonnage
              const merged = organizer ? [organizer, ...apiParticipants] : apiParticipants;
              const seen = new Set();
              const list = merged.filter((p) => {
                if (!p?.user_id) return false;
                if (seen.has(p.user_id)) return false;
                seen.add(p.user_id);
                return true;
              });

              if (list.length === 0) {
                return <p style={{ color: "#555" }}>{t('map.participants.noParticipants')}</p>;
              }

              return (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {list.map((p) => (
                    <li
                      key={p.user_id}
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        marginBottom: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        textAlign: "left",
                      }}
                    >
                      <a
                        href={`/profile/${p.user_id}`}
                        style={{
                          textDecoration: "none",
                          color: "var(--primary-blue)",
                          fontWeight: "600",
                          fontSize: "14px",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        {p.users?.first_name} {p.users?.last_name}
                        {p.user_id === selectedDive?.user_id ? ` (${t('map.participants.organizer')})` : ""}
                      </a>
                    </li>
                  ))}
                </ul>
              );
            })()}

            <button
              onClick={() => setShowParticipants(false)}
              style={{
                marginTop: "15px",
                background: "var(--primary-blue)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "8px 16px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {t('common.close')}
            </button>
          </div>

          {/* Styles d'animation globaux */}
          <style>
            {`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}
          </style>
        </div>
      )}

      {/* ----- Bottom-sheet Détails du site ----- */}
      {showSiteDetails && selectedSite && (
        <div
          className="site-sheet-overlay"
          onClick={() => setShowSiteDetails(false)}
        >
          <div className="site-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="site-sheet-handle" />

            <div className="site-sheet-header">
              <div style={{ minWidth: 0 }}>
                <h3 className="site-sheet-title">{selectedSite.name}</h3>
                {selectedSite.canton && (
                  <span className="site-canton-badge">{selectedSite.canton}</span>
                )}
              </div>
              <button
                className="site-sheet-close"
                onClick={() => setShowSiteDetails(false)}
                aria-label={t('common.close')}
                title={t('common.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="site-sheet-body">
              {selectedSite.description ? (
                <p
                  className="site-sheet-desc"
                  dangerouslySetInnerHTML={{ __html: linkify(selectedSite.description) }}
                />
              ) : (
                <p className="site-sheet-desc site-sheet-desc--empty">
                  {t('map.siteDetails.noDescription')}
                </p>
              )}

              <SiteConditions apiUrl={apiUrl} siteId={selectedSite.id} />
            </div>

            <div className="site-sheet-footer">
              <button
                className="site-sheet-plan"
                onClick={() => {
                  setEditingDive(null);
                  setShowSiteDetails(false);
                  setShowModal(true);
                }}
              >
                {t('map.siteDetails.planHere')}
              </button>
            </div>
          </div>

          <style>
            {`
        .site-sheet-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex; justify-content: center; align-items: flex-end;
          z-index: 9999;
          animation: sheetFade 0.25s ease;
        }
        .site-sheet {
          background: #fff;
          width: 100%;
          max-width: 480px;
          max-height: 82dvh;
          display: flex; flex-direction: column;
          border-radius: 20px 20px 0 0;
          box-shadow: 0 -6px 24px rgba(0,0,0,0.18);
          font-family: Inter, Arial, sans-serif;
          color: #333;
          animation: sheetUp 0.32s cubic-bezier(0.22, 1, 0.36, 1);
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        .site-sheet-handle {
          width: 42px; height: 5px; border-radius: 999px;
          background: #d7dee2; margin: 10px auto 4px; flex-shrink: 0;
        }
        .site-sheet-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 12px; padding: 8px 20px 12px; flex-shrink: 0;
        }
        .site-sheet-title {
          margin: 0; font-size: 18px; font-weight: 700;
          color: var(--primary-blue);
          overflow-wrap: anywhere;
        }
        .site-canton-badge {
          display: inline-block; margin-top: 6px;
          background: rgba(0,150,199,0.12);
          color: var(--primary-blue);
          font-size: 12px; font-weight: 600;
          padding: 3px 10px; border-radius: 999px;
        }
        .site-sheet-close {
          flex-shrink: 0;
          display: inline-flex; align-items: center; justify-content: center;
          width: 34px; height: 34px; border-radius: 50%;
          background: #f1f4f6; border: none; cursor: pointer;
          color: #667; transition: background 0.2s ease, color 0.2s ease;
        }
        .site-sheet-close:hover { background: #e3e9ec; color: #334; }
        .site-sheet-body {
          overflow-y: auto; padding: 0 20px 8px; flex: 1 1 auto;
        }
        .site-sheet-desc {
          margin: 0; font-size: 14.5px; line-height: 1.6;
          color: #444; white-space: pre-wrap;
          overflow-wrap: anywhere; word-break: break-word;
        }
        .site-sheet-desc--empty { color: #8aa0a8; font-style: italic; }
        .site-sheet-desc a { color: var(--blue-accent); }
        .site-sheet-footer {
          padding: 12px 20px 16px; flex-shrink: 0;
          border-top: 1px solid #eef2f4;
        }
        .site-sheet-plan {
          width: 100%; background: var(--primary-blue); color: #fff;
          border: none; border-radius: 10px; padding: 13px;
          font-size: 15px; font-weight: 700; cursor: pointer;
          transition: background 0.2s ease;
        }
        .site-sheet-plan:hover { background: var(--primary-blue-dark); }

        @media (min-width: 600px) {
          .site-sheet-overlay { align-items: center; }
          .site-sheet {
            border-radius: 16px; max-height: 80vh;
            box-shadow: 0 10px 40px rgba(0,0,0,0.25);
            animation: sheetPop 0.28s cubic-bezier(0.22, 1, 0.36, 1);
          }
          .site-sheet-handle { display: none; }
        }

        @keyframes sheetFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sheetUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes sheetPop {
          from { transform: translateY(16px) scale(0.98); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}
          </style>
        </div>
      )}

      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      {/* Assistant IA */}
      {AI_ENABLED && <AiAssistant hideButton={isFiltersPanelOpen} />}
    </div>
  );
};

export default Map;
